
<template>
  <v-container class="information-page py-6" fluid>
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8" xl="7">
        <v-card class="information-card" elevation="2">
          <!-- Header -->
          <div class="information-header">
            <div class="information-title-block">
              <div class="information-title-icon">
                <v-icon color="primary" size="28">
                  mdi-scale-balance
                </v-icon>
              </div>

              <div>
                <h1 class="information-title">
                  Impressum
                </h1>

                <p class="information-subtitle">
                  Rechtliche Angaben zum Anbieter
                </p>
              </div>
            </div>
          </div>

          <v-divider />

          <v-card-text class="information-content">
            <!-- Registered office -->
            <section class="legal-section">
              <div class="legal-section-heading">
                <div class="legal-heading-icon">
                  <v-icon color="primary" size="22">
                    mdi-office-building-outline
                  </v-icon>
                </div>

                <div>
                  <h2>Sitz und Registergericht</h2>
                  <p>Angaben zum Unternehmen und Handelsregister</p>
                </div>
              </div>

              <div class="legal-grid">
                <div class="legal-data-block">
                  <div class="legal-label">
                    Unternehmen
                  </div>

                  <address class="legal-value">
                    <strong>
                      Rud. Otto Meyer Technik GmbH &amp; Co. KG
                    </strong>
                    <br />
                    August-Bebel-Allee 1
                    <br />
                    28329 Bremen
                  </address>
                </div>

                <div class="legal-data-block">
                  <div class="legal-label">
                    Handelsregister
                  </div>

                  <div class="legal-value">
                    HRA 27282 HB
                    <br />
                    Amtsgericht Bremen
                  </div>
                </div>
              </div>
            </section>

            <!-- General partner -->
            <section class="legal-section">
              <div class="legal-section-heading">
                <div class="legal-heading-icon">
                  <v-icon color="primary" size="22">
                    mdi-domain
                  </v-icon>
                </div>

                <div>
                  <h2>Persönlich haftende Gesellschafterin</h2>
                  <p>Unternehmens- und Registerangaben</p>
                </div>
              </div>

              <div class="legal-grid">
                <div class="legal-data-block">
                  <div class="legal-label">
                    Gesellschaft
                  </div>

                  <address class="legal-value">
                    <strong>
                      Rud. Otto Meyer Beteiligungs GmbH
                    </strong>
                    <br />
                    DC Tower 1
                    <br />
                    Donau-City-Straße 7
                    <br />
                    27. OG / Top A
                    <br />
                    1220 Wien
                  </address>
                </div>

                <div class="legal-data-block">
                  <div class="legal-label">
                    Firmenbuch
                  </div>

                  <div class="legal-value">
                    FN 483271 v
                    <br />
                    Handelsgericht Wien
                  </div>
                </div>
              </div>
            </section>

            <!-- Managing directors -->
            <section class="legal-section">
              <div class="legal-section-heading">
                <div class="legal-heading-icon">
                  <v-icon color="primary" size="22">
                    mdi-account-tie-outline
                  </v-icon>
                </div>

                <div>
                  <h2>Geschäftsführer</h2>
                  <p>Vertretungsberechtigte Personen</p>
                </div>
              </div>

              <div class="management-list">
                <v-chip
                  v-for="manager in managers"
                  :key="manager"
                  color="primary"
                  variant="tonal"
                  size="large"
                >
                  <v-icon start size="18">
                    mdi-account-outline
                  </v-icon>

                  {{ manager }}
                </v-chip>
              </div>
            </section>

            <!-- Contact reference -->
            <v-alert
              type="info"
              variant="tonal"
              border="start"
              class="legal-contact-alert"
            >
              <template #title>
                Rückfragen zu EARNS
              </template>

              Bei Fragen zur Anwendung erreichen Sie das EARNS-Team unter
              <a
                href="mailto:earns@rom-technik.de"
                class="legal-link"
              >
                earns@rom-technik.de
              </a>.
            </v-alert>
          </v-card-text>

          <v-divider />

          <div class="information-footer">
            <v-btn
              color="primary"
              variant="tonal"
              :to="{ name: 'Home' }"
            >
              <v-icon start>mdi-home-outline</v-icon>
              Zur Startseite
            </v-btn>

            <v-btn
              color="primary"
              variant="elevated"
              :to="{ name: 'Contact' }"
            >
              <v-icon start>mdi-email-outline</v-icon>
              Kontakt
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts" setup>
const managers = [
  'Ralf H. Behn',
  'Jürgen Sautter',
  'Burkhard Schmidt',
  'Frank Zulauf',
]
</script>


<style scoped lang="scss">
.information-page {
  min-height: 100%;
}

.information-card {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 28px;

  color: rgb(var(--v-theme-on-surface));
  background-color: rgb(var(--v-theme-surface));
}

/* ========================================
   Header
======================================== */

.information-header {
  padding: 1.25rem 1.5rem;

  background: linear-gradient(
    180deg,
    rgba(var(--v-theme-primary), 0.07),
    rgba(var(--v-theme-primary), 0.025)
  );
}

.information-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.information-title-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 48px;
  height: 48px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.09);
}

.information-title {
  margin: 0;

  color: rgb(var(--v-theme-on-surface));

  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
}

.information-subtitle {
  margin: 0.15rem 0 0;

  color: rgba(var(--v-theme-on-surface), 0.62);

  font-size: 0.82rem;
  line-height: 1.4;
}

/* ========================================
   Content
======================================== */

.information-content {
  display: grid;
  gap: 1.25rem;

  padding: 1.5rem;
}

/* ========================================
   Legal sections
======================================== */

.legal-section {
  overflow: hidden;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 22px;

  background-color: rgba(var(--v-theme-primary), 0.025);
}

.legal-section-heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  padding: 0.9rem 1rem;

  border-bottom: 1px solid
    rgba(var(--v-theme-on-surface), 0.07);

  background-color: rgba(var(--v-theme-primary), 0.045);
}

.legal-heading-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;
  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.09);
}

.legal-section-heading h2 {
  margin: 0;

  color: rgb(var(--v-theme-on-surface));

  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
}

.legal-section-heading p {
  margin: 0.1rem 0 0;

  color: rgba(var(--v-theme-on-surface), 0.6);

  font-size: 0.76rem;
  line-height: 1.35;
}

/* ========================================
   Legal data
======================================== */

.legal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.legal-data-block {
  padding: 1rem;
}

.legal-data-block + .legal-data-block {
  border-left: 1px solid
    rgba(var(--v-theme-on-surface), 0.07);
}

.legal-label {
  margin-bottom: 0.4rem;

  color: rgb(var(--v-theme-primary));

  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.legal-value {
  margin: 0;

  color: rgba(var(--v-theme-on-surface), 0.82);

  font-size: 0.9rem;
  font-style: normal;
  line-height: 1.65;
}

.legal-value strong {
  color: rgb(var(--v-theme-on-surface));
}

/* ========================================
   Managers
======================================== */

.management-list {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;

  padding: 1rem;
}

/* ========================================
   Contact alert
======================================== */

.legal-contact-alert {
  border-radius: 20px;
}

.legal-link {
  color: rgb(var(--v-theme-primary));

  font-weight: 700;
  text-decoration: none;
  text-underline-offset: 3px;
}

.legal-link:hover {
  text-decoration: underline;
}

/* ========================================
   Footer
======================================== */

.information-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;

  padding: 1rem 1.5rem;
}

.information-footer .v-btn {
  font-weight: 600;
  text-transform: none;
}

/* ========================================
   Responsive
======================================== */

@media (max-width: 700px) {
  .information-page {
    padding-inline: 0.5rem;
  }

  .information-header {
    padding: 1rem;
  }

  .information-title-block,
  .legal-section-heading {
    align-items: flex-start;
  }

  .information-title-icon {
    width: 42px;
    height: 42px;
  }

  .information-content {
    padding: 1rem;
  }

  .legal-section {
    border-radius: 19px;
  }

  .legal-grid {
    grid-template-columns: 1fr;
  }

  .legal-data-block + .legal-data-block {
    border-top: 1px solid
      rgba(var(--v-theme-on-surface), 0.07);
    border-left: 0;
  }

  .management-list {
    align-items: stretch;
    flex-direction: column;
  }

  .management-list .v-chip {
    justify-content: flex-start;
    width: 100%;
  }

  .information-footer {
    align-items: stretch;
    flex-direction: column-reverse;

    padding: 1rem;
  }

  .information-footer .v-btn {
    width: 100%;
  }
}
</style>